/**
 * Dance floor shape. Rendered with a subtle parquet/checker pattern to
 * visually distinguish it from a plain rectangle at a glance — this is
 * the canvas's signature visual element per the frontend design plan.
 */

import { Group, Rect, Line, Text } from 'react-konva';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

const TILE_SIZE_PX = 18;

interface DanceFloorProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  /** See ObjectRenderer.tsx and RoundTable.tsx for why this exists —
   * counter-rotates the label only, against the room's view orientation. */
  roomRotationDegrees: number;
}

export function DanceFloor({ object, catalogEntry, isSelected, onSelect, onDragEnd, roomRotationDegrees }: DanceFloorProps) {
  const widthPx = catalogEntry.defaultWidthFt * FEET_TO_PX;
  const heightPx = catalogEntry.defaultHeightFt * FEET_TO_PX;

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  const verticalLines: number[] = [];
  for (let x = TILE_SIZE_PX; x < widthPx; x += TILE_SIZE_PX) {
    verticalLines.push(x);
  }
  const horizontalLines: number[] = [];
  for (let y = TILE_SIZE_PX; y < heightPx; y += TILE_SIZE_PX) {
    horizontalLines.push(y);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const container = e.target.getStage()?.container();
        if (container) container.style.cursor = 'default';
      }}
    >
      <Rect
        width={widthPx}
        height={heightPx}
        fill="#F3E6C8"
        stroke={isSelected ? '#C9A35C' : '#B08A42'}
        strokeWidth={isSelected ? 3 : 1.5}
      />

      {verticalLines.map((x) => (
        <Line key={`v-${x}`} points={[x, 0, x, heightPx]} stroke="#E6CC97" strokeWidth={1} />
      ))}
      {horizontalLines.map((y) => (
        <Line key={`h-${y}`} points={[0, y, widthPx, y]} stroke="#E6CC97" strokeWidth={1} />
      ))}

      <Text
        text={object.label}
        fontSize={13}
        fontFamily="Fraunces, serif"
        fill="#6B5227"
        width={widthPx}
        height={heightPx}
        x={widthPx / 2}
        y={heightPx / 2}
        offsetX={widthPx / 2}
        offsetY={heightPx / 2}
        rotation={-roomRotationDegrees}
        align="center"
        verticalAlign="middle"
        listening={false}
      />

      {object.locked && (
        <Text
          text={'\u{1F512}'}
          fontSize={13}
          x={widthPx / 2}
          y={-16}
          offsetX={6.5}
          offsetY={6.5}
          listening={false}
        />
      )}
    </Group>
  );
}
